import { isSimplePolygonLatLng } from './maps_helper'

let _ShapeEditHandlesClass = null;

function defineClass() {
  return class ShapeEditHandles extends google.maps.OverlayView {
    constructor(map, editor) {
      super();
      this.map = map;
      this.editor = editor;

      this.container = null;
      this.shape = null;
      this.mode = null; // 'drawing' | 'editing'
      this.shapeType = null;

      this._handles = [];
      this._polygonFinishHandler = null;
      this._raf = null;
      this._activeDragCleanup = null;
      this._savedCursor = null;
      this._savedBodyCursor = null;
    }

    onAdd() {
      this.container = document.createElement('div');
      this.container.className = 'shapeEditorHandleLayer';
      const panes = this.getPanes();
      panes.overlayMouseTarget.appendChild(this.container);
    }

    onRemove() {
      this.clear();
      if (this.container && this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
      this.container = null;
    }

    attach(shape, { mode, shapeType }) {
      this.shape = shape;
      this.mode = mode;
      this.shapeType = shapeType;
      this.requestDraw();
    }

    clear() {
      this._polygonFinishHandler = null;
      this.shape = null;
      this.mode = null;
      this.shapeType = null;
      this._handles.forEach((h) => h.el && h.el.remove && h.el.remove());
      this._handles = [];
    }

    setPolygonFinishHandler(fn) {
      this._polygonFinishHandler = fn;
    }

    requestDraw() {
      if (this._raf) return;
      // Coalesce rapid calls into one DOM update per display frame. ie dont draw a bunch of stuff the user cannot possibly see.
      this._raf = window.requestAnimationFrame(() => {
        this._raf = null;
        this.draw();
      });
    }

    draw() {
      if (!this.container || !this.shape || !this.getProjection()) return;
      this._rebuildHandles();
      this._positionHandles();
    }

    _rebuildHandles() {
      this._handles.forEach((h) => h.el && h.el.remove && h.el.remove());
      this._handles = [];

      if (this.shapeType === 'polygon') {
        this._buildPolygonHandles();
        return;
      }
      if (this.shapeType === 'rectangle') {
        this._buildRectangleHandles();
        return;
      }
      if (this.shapeType === 'circle') {
        this._buildCircleHandles();
        return;
      }
    }

    _buildPolygonHandles() {
      if (!this.shape.getPath) return;
      const path = this.shape.getPath();
      const points = path.getArray ? path.getArray() : [];

      for (let i = 0; i < points.length; i++) {
        const vertexIndex = i;
        const isFirst = i === 0;
        const canFinish = this.mode === 'drawing' && isFirst && points.length >= this.editor.options.polygonFinishMinVertices;

        const el = document.createElement('div');
        el.className = `shapeEditorHandle vertex${canFinish ? ' finish' : ''}`;
        el.dataset.handleType = 'vertex';
        el.dataset.index = String(i);

        if (canFinish) {
          el.addEventListener('click', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            if (typeof this._polygonFinishHandler === 'function') this._polygonFinishHandler();
          }, true);
        }

        if (this.mode === 'editing') {
          this._attachPolygonVertexDrag(el, i);
          this._attachPolygonVertexDelete(el, i);
        }
        this.container.appendChild(el);
        this._handles.push({
          type: 'vertex',
          index: vertexIndex,
          el,
          getLatLng: () => {
            const p = this.shape.getPath();
            const arr = p && typeof p.getArray === 'function' ? p.getArray() : [];
            return arr[vertexIndex];
          }
        });
      }

      if (this.mode === 'editing' && points.length >= 3) {
        for (let i = 0; i < points.length; i++) {
          const a = points[i];
          const b = points[(i + 1) % points.length];
          const mid = new google.maps.LatLng((a.lat() + b.lat()) / 2, (a.lng() + b.lng()) / 2);
          const el = document.createElement('div');
          el.className = 'shapeEditorHandle midpoint';
          el.dataset.handleType = 'midpoint';
          el.dataset.index = String(i);
          this._attachPolygonMidpointInsertDrag(el, i, mid);
          this.container.appendChild(el);
          this._handles.push({ type: 'midpoint', index: i, el, getLatLng: () => mid });
        }

        const center = this.editor._shapeCenter(this.shape, 'polygon');
        if (center) {
          const el = document.createElement('div');
          el.className = 'shapeEditorHandle center';
          el.dataset.handleType = 'center';
          this._attachPolygonCenterDrag(el);
          this.container.appendChild(el);
          this._handles.push({ type: 'center', el, getLatLng: () => this.editor._shapeCenter(this.shape, 'polygon') });
        }
      }
    }

    _attachPolygonVertexDelete(el, index) {
      const tryDelete = (evt) => {
        const path = this.shape.getPath();
        if (!path) return;
        const len = (typeof path.getLength === 'function') ? path.getLength() : (path.getArray ? path.getArray().length : 0);
        if (len <= 3) return;

        if (evt && evt.preventDefault) evt.preventDefault();
        if (evt && evt.stopPropagation) evt.stopPropagation();

        const before = this.editor._pushUndoSnapshot();
        if (typeof path.removeAt === 'function') {
          path.removeAt(index);
        } else if (path.getArray) {
          const arr = path.getArray();
          arr.splice(index, 1);
          if (typeof this.shape.setPath === 'function') this.shape.setPath(arr);
        }
        if (!this._validatePolygonOrRevert(before)) {
          return;
        }
        this.editor._change(this.shape);
        this.requestDraw();
      };

      el.addEventListener('contextmenu', tryDelete, true);
      el.addEventListener('click', (evt) => {
        if (evt && evt.shiftKey) {
          tryDelete(evt);
        }
      }, true);
    }

    _validatePolygonOrRevert(beforeSnapshot) {
      try {
        const path = this.shape.getPath();
        const pts = path && typeof path.getArray === 'function' ? path.getArray() : [];
        if (pts.length < 3) return true;
        if (isSimplePolygonLatLng(pts)) return true;

        if (beforeSnapshot) {
          this.editor._applySnapshot(this.shape, beforeSnapshot);
        }
        this.editor._discardLastUndoSnapshot();
        this.editor._change(this.shape);
        this.requestDraw();
        this.editor._error(new Error('Invalid polygon: self-intersection'));
        return false;
      } catch (e) {
        return true;
      }
    }

    _buildRectangleHandles() {
      if (this.mode !== 'editing') return;
      if (!this.shape.getBounds) return;

      const bounds = this.shape.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const nw = new google.maps.LatLng(ne.lat(), sw.lng());
      const se = new google.maps.LatLng(sw.lat(), ne.lng());
      const corners = [
        { key: 'nw', latLng: nw },
        { key: 'ne', latLng: ne },
        { key: 'se', latLng: se },
        { key: 'sw', latLng: sw },
      ];

      corners.forEach(({ key, latLng }) => {
        const el = document.createElement('div');
        el.className = 'shapeEditorHandle corner';
        el.dataset.handleType = 'corner';
        el.dataset.cornerKey = key;
        this._attachRectangleCornerDrag(el, key);
        this.container.appendChild(el);
        this._handles.push({ type: 'corner', key, el, getLatLng: () => this._rectangleCornerLatLng(key) });
      });

      const center = this.editor._shapeCenter(this.shape, 'rectangle');
      if (center) {
        const el = document.createElement('div');
        el.className = 'shapeEditorHandle center';
        el.dataset.handleType = 'center';
        this._attachRectangleCenterDrag(el);
        this.container.appendChild(el);
        this._handles.push({ type: 'center', el, getLatLng: () => this.editor._shapeCenter(this.shape, 'rectangle') });
      }
    }

    _buildCircleHandles() {
      if (this.mode !== 'editing') return;
      if (!this.shape.getCenter || !this.shape.getRadius) return;

      const center = this.shape.getCenter();
      const elCenter = document.createElement('div');
      elCenter.className = 'shapeEditorHandle center';
      elCenter.dataset.handleType = 'center';
      this._attachCircleCenterDrag(elCenter);
      this.container.appendChild(elCenter);
      this._handles.push({ type: 'center', el: elCenter, getLatLng: () => this.shape.getCenter() });

      const headings = [
        { name: 'n', deg: 0 },
        { name: 'e', deg: 90 },
        { name: 's', deg: 180 },
        { name: 'w', deg: 270 },
      ];
      headings.forEach(({ name, deg }) => {
        const elRadius = document.createElement('div');
        elRadius.className = 'shapeEditorHandle radius';
        elRadius.dataset.handleType = 'radius';
        elRadius.dataset.heading = String(deg);
        elRadius.dataset.headingName = name;
        this._attachCircleRadiusDrag(elRadius);
        this.container.appendChild(elRadius);
        this._handles.push({
          type: 'radius',
          heading: deg,
          el: elRadius,
          getLatLng: () => this._circleRadiusHandleLatLng(this.shape.getCenter(), this.shape.getRadius(), deg)
        });
      });
    }

    _positionHandles() {
      const proj = this.getProjection();
      for (let i = 0; i < this._handles.length; i++) {
        const h = this._handles[i];
        const latLng = h.getLatLng ? h.getLatLng() : null;
        if (!latLng) continue;
        const p = proj.fromLatLngToDivPixel(latLng);
        if (!p) continue;
        h.el.style.left = `${p.x}px`;
        h.el.style.top = `${p.y}px`;
      }
    }

    /* -------------------------- Drag plumbing -------------------------- */

    _mapDiv() {
      try {
        const panes = this.getPanes && this.getPanes();
        if (panes && panes.overlayMouseTarget) return panes.overlayMouseTarget;
      } catch (e) {
        // ignore
      }
      return this.map && this.map.getDiv ? this.map.getDiv() : null;
    }

    _clientToDivPixel(evt) {
      const div = this._mapDiv();
      if (!div) return null;
      const rect = div.getBoundingClientRect();
      const clientX = evt.touches && evt.touches[0] ? evt.touches[0].clientX : evt.clientX;
      const clientY = evt.touches && evt.touches[0] ? evt.touches[0].clientY : evt.clientY;
      if (typeof clientX !== 'number' || typeof clientY !== 'number') return null;

      return new google.maps.Point(clientX - rect.left, clientY - rect.top);
    }

    _eventToLatLng(evt) {
      const proj = this.getProjection();
      const p = this._clientToDivPixel(evt);
      if (!proj || !p || !proj.fromDivPixelToLatLng) return null;
      return proj.fromDivPixelToLatLng(p);
    }

    _startMapLatLngDrag(evt, { onMoveLatLng, onEnd }) {
      if (evt.preventDefault) evt.preventDefault();
      if (evt.stopPropagation) evt.stopPropagation();

      if (typeof this._activeDragCleanup === 'function') {
        this._activeDragCleanup();
        this._activeDragCleanup = null;
      }

      this._setDraggingCursor(true);

      if (this.map && this.map.setOptions) this.map.setOptions({ draggable: false });

      const mapDiv = this.map && this.map.getDiv ? this.map.getDiv() : null;
      const savedTouchAction = mapDiv ? (mapDiv.style.touchAction || '') : '';
      if (mapDiv) mapDiv.style.touchAction = 'none';

      const tempWin = [];
      const addWin = (eventName, handler, options) => {
        const opts = options || true;
        window.addEventListener(eventName, handler, opts);
        tempWin.push({ eventName, handler, opts });
      };
      const removeWin = () => {
        tempWin.forEach(({ eventName, handler, opts }) => window.removeEventListener(eventName, handler, opts));
        tempWin.length = 0;
      };

      let moveListener = null;
      let upListener = null;
      let lastMapMoveAt = 0;

      const cleanup = () => {
        removeWin();
        if (moveListener) google.maps.event.removeListener(moveListener);
        if (upListener) google.maps.event.removeListener(upListener);
        moveListener = null;
        upListener = null;
        this._setDraggingCursor(false);
        if (mapDiv) mapDiv.style.touchAction = savedTouchAction;
        if (this.map && this.map.setOptions) this.map.setOptions({ draggable: true });
      };

      let ended = false;
      const end = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        if (ended) return;
        ended = true;

        // In Chrome responsive/touch mode, pointerup fires before touchend.
        // cleanup() removes ALL window listeners, so the touchend listener is
        // gone before touchend fires. An un-prevented touchend causes the
        // browser to synthesize mousedown→mouseup→click on the map, which
        // disrupts subsequent touch interactions with Google Maps controls
        // (toolbar buttons need two taps instead of one).
        //
        // Fix: add short-lived suppressors BEFORE cleanup removes our
        // listeners. They prevent+stop any remaining end events in this
        // interaction. setTimeout(0) removes them on the next tick, after all
        // events for this pointer sequence have been dispatched.
        const suppress = (ev) => {
          if (ev.preventDefault) ev.preventDefault();
          if (ev.stopPropagation) ev.stopPropagation();
        };
        const suppressedEvents = ['touchend', 'pointerup', 'mouseup', 'click'];
        suppressedEvents.forEach((n) => window.addEventListener(n, suppress, true));
        setTimeout(() => {
          suppressedEvents.forEach((n) => window.removeEventListener(n, suppress, true));
        }, 0);

        cleanup();
        this._activeDragCleanup = null;
        if (typeof onEnd === 'function') onEnd(e);
        this.requestDraw();
      };

      moveListener = this.map.addListener('mousemove', (e) => {
        if (!e || !e.latLng) return;
        lastMapMoveAt = Date.now();
        if (typeof onMoveLatLng === 'function') onMoveLatLng(e.latLng, e);
        this.requestDraw();
      });
      upListener = this.map.addListener('mouseup', end);

      let lastWinMoveAt = 0;
      const winMove = (e) => {
        if (e.cancelable && e.preventDefault) e.preventDefault();
        const now = Date.now();
        if (now - lastWinMoveAt < 16) return;
        if (now - lastMapMoveAt < 50) return;
        lastWinMoveAt = now;
        const latLng = this._eventToLatLng(e);
        if (!latLng) return;
        if (typeof onMoveLatLng === 'function') onMoveLatLng(latLng, e);
        this.requestDraw();
      };
      addWin('mousemove', winMove);
      addWin('pointermove', winMove, { capture: true, passive: false });
      addWin('touchmove', winMove, { capture: true, passive: false });

      addWin('mouseup', end);
      addWin('pointerup', end);
      addWin('pointercancel', end);
      addWin('touchend', end);
      addWin('touchcancel', end);

      this._activeDragCleanup = cleanup;
    }

    _setDraggingCursor(isDragging) {
      const div = this.map && this.map.getDiv ? this.map.getDiv() : null;
      const body = (typeof document !== 'undefined') ? document.body : null;
      if (!div || !div.style) return;
      if (isDragging) {
        if (this._savedCursor === null) {
          this._savedCursor = div.style.cursor || '';
        }
        if (body && body.style && this._savedBodyCursor === null) {
          this._savedBodyCursor = body.style.cursor || '';
        }
        div.style.cursor = 'move';
        if (body && body.style) body.style.cursor = 'move';
      } else {
        if (this._savedCursor !== null) {
          div.style.cursor = this._savedCursor;
          this._savedCursor = null;
        }
        if (body && body.style && this._savedBodyCursor !== null) {
          body.style.cursor = this._savedBodyCursor;
          this._savedBodyCursor = null;
        }
      }
    }

    _onGrab(el, handler) {
      let lastGrabAt = 0;
      const guarded = (evt) => {
        const now = Date.now();
        if (now - lastGrabAt < 300) {
          if (evt.cancelable && evt.preventDefault) evt.preventDefault();
          return;
        }
        lastGrabAt = now;
        handler(evt);
      };
      el.addEventListener('pointerdown', guarded, true);
      el.addEventListener('mousedown', guarded, true);
      el.addEventListener('touchstart', guarded, { capture: true, passive: false });
    }

    /* -------------------------- Polygon editing -------------------------- */

    _attachPolygonVertexDrag(el, index) {
      const down = (evt) => {
        const before = this.editor._pushUndoSnapshot();
        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            const path = this.shape.getPath();
            if (path && typeof path.setAt === 'function') {
              path.setAt(index, latLng);
            } else if (path && typeof path.getArray === 'function') {
              const arr = path.getArray();
              arr[index] = latLng;
              if (typeof this.shape.setPath === 'function') this.shape.setPath(arr);
            }
            this.editor._change(this.shape);
          },
          onEnd: () => this._validatePolygonOrRevert(before)
        });
      };
      this._onGrab(el, down);
    }

    _attachPolygonMidpointInsertDrag(el, segmentIndex, initialLatLng) {
      const down = (evt) => {
        const before = this.editor._pushUndoSnapshot();
        const path = this.shape.getPath();
        const insertAt = segmentIndex + 1;
        if (path && typeof path.insertAt === 'function') {
          path.insertAt(insertAt, initialLatLng);
        } else if (path && typeof path.getArray === 'function') {
          const arr = path.getArray();
          arr.splice(insertAt, 0, initialLatLng);
          if (typeof this.shape.setPath === 'function') this.shape.setPath(arr);
        }
        this.editor._change(this.shape);

        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            const p = this.shape.getPath();
            if (p && typeof p.setAt === 'function') {
              p.setAt(insertAt, latLng);
            } else if (p && typeof p.getArray === 'function') {
              const arr = p.getArray();
              arr[insertAt] = latLng;
              if (typeof this.shape.setPath === 'function') this.shape.setPath(arr);
            }
            this.editor._change(this.shape);
          },
          onEnd: () => this._validatePolygonOrRevert(before)
        });
      };
      this._onGrab(el, down);
    }

    _attachPolygonCenterDrag(el) {
      const down = (evt) => {
        let lastLatLng = null;
        const before = this.editor._pushUndoSnapshot();
        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            const path = this.shape.getPath();
            if (!path) return;
            if (!lastLatLng) {
              lastLatLng = latLng;
              return;
            }

            const dLat = latLng.lat() - lastLatLng.lat();
            const dLng = latLng.lng() - lastLatLng.lng();
            lastLatLng = latLng;

            const len = (typeof path.getLength === 'function') ? path.getLength() : (path.getArray ? path.getArray().length : 0);
            for (let i = 0; i < len; i++) {
              const cur = (typeof path.getAt === 'function') ? path.getAt(i) : path.getArray()[i];
              const moved = new google.maps.LatLng(cur.lat() + dLat, cur.lng() + dLng);
              if (typeof path.setAt === 'function') {
                path.setAt(i, moved);
              }
            }
            this.editor._change(this.shape);
          },
          onEnd: () => this._validatePolygonOrRevert(before)
        });
      };
      this._onGrab(el, down);
    }

    /* -------------------------- Rectangle editing -------------------------- */

    _rectangleCornerLatLng(key) {
      const bounds = this.shape.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      if (key === 'ne') return ne;
      if (key === 'sw') return sw;
      if (key === 'nw') return new google.maps.LatLng(ne.lat(), sw.lng());
      if (key === 'se') return new google.maps.LatLng(sw.lat(), ne.lng());
      return null;
    }

    _attachRectangleCornerDrag(el, cornerKey) {
      const down = (evt) => {
        this.editor._pushUndoSnapshot();
        const bounds = this.shape.getBounds();
        const ne0 = bounds.getNorthEast();
        const sw0 = bounds.getSouthWest();
        const fixed = {
          nw: new google.maps.LatLng(sw0.lat(), ne0.lng()),
          ne: sw0,
          se: new google.maps.LatLng(ne0.lat(), sw0.lng()),
          sw: ne0,
        }[cornerKey];

        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            const south = Math.min(fixed.lat(), latLng.lat());
            const north = Math.max(fixed.lat(), latLng.lat());
            const west = Math.min(fixed.lng(), latLng.lng());
            const east = Math.max(fixed.lng(), latLng.lng());
            const sw = new google.maps.LatLng(south, west);
            const ne = new google.maps.LatLng(north, east);
            this.shape.setBounds(new google.maps.LatLngBounds(sw, ne));
            this.editor._change(this.shape);
          }
        });
      };
      this._onGrab(el, down);
    }

    _attachRectangleCenterDrag(el) {
      const down = (evt) => {
        let lastLatLng = null;
        this.editor._pushUndoSnapshot();
        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            if (!lastLatLng) {
              lastLatLng = latLng;
              return;
            }
            const dLat = latLng.lat() - lastLatLng.lat();
            const dLng = latLng.lng() - lastLatLng.lng();
            lastLatLng = latLng;

            const b = this.shape.getBounds();
            const ne0 = b.getNorthEast();
            const sw0 = b.getSouthWest();
            const ne = new google.maps.LatLng(ne0.lat() + dLat, ne0.lng() + dLng);
            const sw = new google.maps.LatLng(sw0.lat() + dLat, sw0.lng() + dLng);
            this.shape.setBounds(new google.maps.LatLngBounds(sw, ne));
            this.editor._change(this.shape);
          }
        });
      };
      this._onGrab(el, down);
    }

    /* -------------------------- Circle editing -------------------------- */

    _circleRadiusHandleLatLng(center, radiusMeters, headingDegrees) {
      const g = (typeof google !== 'undefined') ? google : null;
      if (g && g.maps && g.maps.geometry && g.maps.geometry.spherical && typeof g.maps.geometry.spherical.computeOffset === 'function') {
        return g.maps.geometry.spherical.computeOffset(center, radiusMeters, headingDegrees);
      }

      const heading = (headingDegrees || 0) * Math.PI / 180;
      const dLatMeters = radiusMeters * Math.cos(heading);
      const dLngMeters = radiusMeters * Math.sin(heading);
      const dLat = dLatMeters / 111320;
      const latRad = center.lat() * Math.PI / 180;
      const metersPerDegLng = 111320 * Math.cos(latRad);
      const dLng = metersPerDegLng > 0 ? (dLngMeters / metersPerDegLng) : 0;
      return new google.maps.LatLng(center.lat() + dLat, center.lng() + dLng);
    }

    _attachCircleCenterDrag(el) {
      const down = (evt) => {
        this.editor._pushUndoSnapshot();
        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            this.shape.setCenter(latLng);
            this.editor._change(this.shape);
          }
        });
      };
      this._onGrab(el, down);
    }

    _attachCircleRadiusDrag(el) {
      const down = (evt) => {
        this.editor._pushUndoSnapshot();
        this._startMapLatLngDrag(evt, {
          onMoveLatLng: (latLng) => {
            const center = this.shape.getCenter();
            const radius = this.editor._distanceBetween(center, latLng);
            this.shape.setRadius(radius);
            this.editor._change(this.shape);
          }
        });
      };
      this._onGrab(el, down);
    }
  };
}

export default function createShapeEditHandles(map, editor) {
  if (!_ShapeEditHandlesClass) _ShapeEditHandlesClass = defineClass();
  return new _ShapeEditHandlesClass(map, editor);
}
